// BottomBar.tsx
import React, { useState } from 'react';
import { Home, ShoppingBag, Newspaper, Menu, X, ChevronDown, ShoppingCart,Contact } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { CartButton } from '../../../../ui/elements/CartButton';

const BottomBar: React.FC = () => {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [openDropdown, setOpenDropdown] = useState<string | null>(null);
    const navigate = useNavigate();

    // Données des liens pour le menu latéral (sans Accueil ni Produits, avec Centre d'aide)
    const bottomBarNavItems = [
        {
            name: 'À propos',
            to: '/about',
            subLinks: [
                { name: 'Notre histoire', to: '/about' },
                { name: 'Notre équipe', to: '/about' }
            ]
        },
        {
            name: 'Contact',
            to: '/contact',
            subLinks: [
                { name: 'Email', to: '/contact' },
                { name: 'Téléphone', to: '/contact' }
            ]
        },
        {
            name: 'Centre d’aide',
            to: '/help',
            subLinks: [
                { name: 'FAQ', to: '/help' },
                { name: 'Assistance', to: '/help' }
            ]
        }
    ];

    const toggleDropdown = (name: string) => {
        setOpenDropdown(openDropdown === name ? null : name);
    };

    const closeSidebar = () => {
        setSidebarOpen(false);
        setOpenDropdown(null);
    };

    return (
        <>
            {/* Barre inférieure fixe (mobile uniquement) */}
            <div className="fixed bottom-0 left-0 right-0 bg-green-800 shadow-lg border-t border-white/20 backdrop-blur-md z-50 md:hidden">
                <div className="flex justify-between items-center py-2 px-4">
                    <button
                        onClick={() => navigate('/')}
                        className="flex flex-col items-center gap-1 text-white/80 hover:text-accent transition-colors"
                    >
                        <Home size={24} />
                        <span className="text-[10px]">Accueil</span>
                    </button>

                    <button
                        onClick={() => navigate('/products')}
                        className="flex flex-col items-center gap-1 text-white/80 hover:text-accent transition-colors"
                    >
                        <ShoppingBag size={24} />
                        <span className="text-[10px]">Boutique</span>
                    </button>

                    <button
                        onClick={() => navigate('/blog')}
                        className="flex flex-col items-center gap-1 text-white/80 hover:text-accent transition-colors"
                    >
                        <Newspaper size={24} />
                        <span className="text-[10px]">Blog</span>
                    </button>
                    <button
                        onClick={() => navigate('/contact')}
                        className="flex flex-col items-center gap-1 text-white/80 hover:text-accent transition-colors"
                    >
                        <CartButton />
                        <span className="text-[10px]">Contact</span>
                    </button>
                    <button
                        onClick={() => setSidebarOpen(true)}
                        className="flex flex-col items-center gap-1 text-white/80 hover:text-accent transition-colors"
                    >
                        <Menu size={24} />
                        <span className="text-[10px]">Menu</span>
                    </button>
                </div>
            </div>

            {/* SIDEBAR - avec les liens personnalisés */}
            <div
                className={`fixed top-0 right-0 h-full w-80 bg-secondary shadow-2xl transform transition-transform duration-500 ease-in-out z-50 md:hidden ${sidebarOpen ? 'translate-x-0' : 'translate-x-full'
                    }`}
            >
                <div className="p-5 flex justify-between items-center border-b border-white/20">
                    <span className="text-xl font-bold text-white">Menu</span>
                    <button
                        onClick={closeSidebar}
                        className="p-2 bg-accent rounded-full hover:scale-105 transition-transform duration-200"
                    >
                        <X size={20} className="text-primary" />
                    </button>
                </div>

                <nav className="flex flex-col gap-3 p-5 text-white">
                    <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 space-y-3">
                        {bottomBarNavItems.map((item) => (
                            <div key={item.name} className="border-b border-white/20 pb-2 last:border-0">
                                <div className="flex items-center justify-between">
                                    <Link
                                        to={item.to}
                                        onClick={closeSidebar}
                                        className="flex-1 py-2 text-lg font-medium hover:text-accent transition-colors duration-200 text-left"
                                    >
                                        {item.name}
                                    </Link>
                                    {item.subLinks && item.subLinks.length > 0 && (
                                        <button onClick={() => toggleDropdown(item.name)} className="p-2">
                                            <ChevronDown
                                                size={18}
                                                className={`transition-transform duration-300 ${openDropdown === item.name ? 'rotate-180' : ''
                                                    }`}
                                            />
                                        </button>
                                    )}
                                </div>

                                {item.subLinks && item.subLinks.length > 0 && (
                                    <div
                                        className={`pl-4 flex flex-col gap-2 mt-1 overflow-hidden transition-all duration-300 ${openDropdown === item.name ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'
                                            }`}
                                    >
                                        {item.subLinks.map((link) => (
                                            <Link
                                                key={link.name}
                                                to={link.to}
                                                onClick={closeSidebar}
                                                className="text-sm opacity-80 hover:opacity-100 hover:text-accent transition"
                                            >
                                                {link.name}
                                            </Link>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    <div className="mt-4">
                        <button
                            onClick={() => {
                                navigate('/products');
                                closeSidebar();
                            }}
                            className="bg-accent text-primary font-bold py-3 rounded-full w-full flex items-center justify-center gap-2 hover:scale-105 transition-transform duration-200"
                        >
                            <ShoppingCart size={18} /> Commander
                        </button>
                    </div>
                </nav>
            </div>

            {sidebarOpen && (
                <div className="fixed inset-0 bg-black/50 z-40 md:hidden" onClick={closeSidebar} />
            )}
        </>
    );
};

export default BottomBar;